import { rest } from '@karpeleslab/klbfw'

// CMS IDs from the rewrite template
export const CMS_IDS = {
  page: 'ctcm-gdvi63-b5tb-ffhb-hchm-ggifhxuq',
  product: 'ctcm-dhrpbl-ygjj-gqfc-utpj-i2dadrae',
  announcement: 'ctcm-nzonqx-uumb-cc7h-5lub-etgwdqsq',
  article: 'ctcm-nzonqx-uumb-cc7h-5lub-etgwdqsq',
  service: 'ctcm-dhrpbl-ygjj-gqfc-utpj-i2dadrae', // Using same ID as product
  // restDoc removed - using RestApiDocs component instead
}

/**
 * Process CMS response from loadSlug/load API
 * loadSlug API returns: {"data": {"content_cms": <Content/Cms object>, "content_cms_entry_data": <Content/Cms/Entry/Data object>}}
 * Based on the API schema, the Content_Cms_Entry_Data has these key fields:
 * - Content_Cms_Entry_Data__: UUID (PK)
 * - Content_Cms_Entry__: UUID (FK)
 * - Content_Cms__: UUID (FK)
 * - Language__: CHAR(5)
 * - Title: VARCHAR(128)
 * - Short_Contents: VARCHAR(257)
 * - Contents: LONGTEXT
 * - Working_Contents: LONGTEXT
 * - Status: ENUM('draft', 'translating', 'published')
 * 
 * @param {Object} response - The API response
 * @returns {Object} - Processed content data
 */
const processCmsResponse = (response) => {
  if (!response || !response.data) {
    console.error('CMS response missing data property:', response)
    return null
  }

  // Extract CMS data from response
  const { content_cms, content_cms_entry_data } = response.data

  if (!content_cms_entry_data) {
    console.error('CMS response missing content_cms_entry_data:', response.data)
    return null
  }

  // Combine relevant information
  return {
    // From Content_Cms
    Id: content_cms?.Content_Cms__ || '',
    Name: content_cms?.Name || '',
    
    // From Content_Cms_Entry_Data - standard fields
    Title: content_cms_entry_data.Title || '',
    Content: content_cms_entry_data.Contents || '',
    Html: content_cms_entry_data.Contents || '', // For backward compatibility
    ShortContent: content_cms_entry_data.Short_Contents || '',
    Status: content_cms_entry_data.Status || 'draft',
    Language: content_cms_entry_data.Language__ || 'en-US',
    Key: content_cms_entry_data.Key || '',
    
    // Optional date fields
    Created: content_cms_entry_data.Created || null,
    Published: content_cms_entry_data.Published || null,
    LastModified: content_cms_entry_data.Last_Modified || null,
    
    // Common custom fields (may not exist in all entries)
    Image: content_cms_entry_data.Image || '',
    Description: content_cms_entry_data.Description || '',
    MetaTitle: content_cms_entry_data.MetaTitle || '',
    MetaDescription: content_cms_entry_data.MetaDescription || '',
    Category: content_cms_entry_data.Category || '',
    Price: content_cms_entry_data.Price || null,
    CtaUrl: content_cms_entry_data.CtaUrl || '',
    CtaText: content_cms_entry_data.CtaText || '',
    
    // For any arbitrary fields present in the entry
    data: content_cms_entry_data,
    
    // Raw data for advanced needs
    raw_cms: content_cms,
    raw_entry_data: content_cms_entry_data
  }
}

/**
 * Process CMS search response
 * The Content/Cms/:id:search endpoint returns data in this format:
 * {data: {content_cms: Object, data: Array<Object>}}
 * 
 * @param {Object} response - The search API response
 * @returns {Array} - Array of processed content items
 */
const processCmsSearchResponse = (response) => {
  if (!response || !response.data) {
    console.error('Response missing data property:', response)
    return []
  }
  
  // The Content/Cms/:id:search endpoint returns data in this format:
  // {data: {content_cms: Object, data: Array<Object>}}
  if (response.data && response.data.content_cms && response.data.data && Array.isArray(response.data.data)) {
    return response.data.data.map(entryData => {
      return {
        // From Content_Cms
        Id: response.data.content_cms?.Content_Cms__ || '',
        Name: response.data.content_cms?.Name || '',
        
        // From Content_Cms_Entry_Data - standard fields
        Title: entryData.Title || '',
        Content: entryData.Contents || '',
        Html: entryData.Contents || '', 
        ShortContent: entryData.Short_Contents || '',
        Status: entryData.Status || 'draft',
        Language: entryData.Language__ || 'en-US',
        Key: entryData.Slug || entryData.Key || '',
        
        // Optional date fields
        Created: entryData.Created || null,
        Published: entryData.Published || null,
        LastModified: entryData.Last_Modified || null,
        
        // Common custom fields
        Image: entryData.Drive_Item?.Download_Url || entryData.Drive_Item?.View_Url || entryData.Drive_Item__ || '',
        Description: entryData.Description || '',
        MetaTitle: entryData.MetaTitle || '',
        MetaDescription: entryData.MetaDescription || '',
        Category: entryData.Category || '',
        Price: entryData.Price || null,
        CtaUrl: entryData.CtaUrl || '',
        CtaText: entryData.CtaText || '',
        
        // Store original data
        data: entryData,
        raw_cms: response.data.content_cms,
        raw_entry_data: entryData
      };
    }).filter(Boolean);
  }
  
  console.error('Unexpected response format from Content/Cms search API:', response)
  return [];
}

/**
 * Fetch CMS content by type and slug
 * @param {string} type - The type of content (page, product, announcement, article)
 * @param {string} slug - The slug of the content
 * @returns {Promise} - Promise resolving to the processed CMS content
 */
export const fetchCmsContent = async (type, slug) => {
  try {
    const contentId = CMS_IDS[type]
    if (!contentId) {
      throw new Error(`Content type "${type}" not found`)
    }

    const response = await rest(`Content/Cms/${contentId}:loadSlug`, 'POST', { slug })
    const processed = processCmsResponse(response)
    
    if (!processed) {
      throw new Error(`No content found for ${type} with slug "${slug}"`)
    }
    
    return processed
  } catch (error) {
    console.error(`Error fetching ${type} content:`, error)
    throw error
  }
}

/**
 * Fetch multiple CMS entries by type with search params
 * @param {string} type - The type of content (page, product, announcement, article)
 * @param {Object} params - Search parameters
 * @returns {Promise} - Promise resolving to an array of processed CMS content items
 */
export const fetchCmsEntries = async (type, params = {}) => {
  try {
    const contentId = CMS_IDS[type]
    if (!contentId) {
      throw new Error(`Content type "${type}" not found`)
    }

    // Build search parameters
    const searchParams = {
      page_no: params.page || 1,
      results_per_page: params.limit || 10,
      ...params.sort && { sort: params.sort },
      ...params.query && { query: params.query }
    }

    console.log(`Fetching ${type} entries with params:`, searchParams)
    
    const response = await rest(`Content/Cms/${contentId}:search`, 'GET', searchParams)
    return {
      items: processCmsSearchResponse(response),
      pagination: response.paging || {},
      query_time_ms: response.query_ms || 0,
      raw: response // Include raw response for debugging
    }
  } catch (error) {
    console.error(`Error fetching ${type} entries:`, error)
    throw error
  }
}


export default {
  fetchCmsContent,
  fetchCmsEntries
}